/**
 * SPEC-annotation-review-concurrency (Phase 1)
 *
 * Shared TTL + types for the annotation-review claim namespace. The same
 * 10-minute window must be used in:
 *   - the websocket stale-lock checker (server/websocket.ts)
 *   - atomicClaimReviewDelivery's "is the existing claim stale?" SQL
 *     condition (server/storage.ts)
 *   - the queue / neighbors visibility filter (annotation-review-{queue,
 *     neighbors}-handler.ts)
 *
 * Centralising the constant here removes the drift risk the spec calls out
 * explicitly ("the same TTL value lives in three places — pull from a single
 * shared constant").
 *
 * Matches the existing agent-QA-flow precedent: STALE_LOCK_TIMEOUT_MS in
 * server/websocket.ts is also 10 * 60 * 1000. Keeping the two TTLs in lock
 * step is intentional — there is no scenario in which a review-claim TTL
 * should drift from the QA-claim TTL.
 */

/** TTL for an annotation-review claim, in milliseconds. */
export const REVIEW_CLAIM_TTL_MS = 10 * 60 * 1000;

/**
 * Public claim shape returned to clients alongside queue rows when the
 * `include_claimed=1` flag is set, and embedded in `review:update` socket
 * events. Times are ISO-8601 strings on the wire (Date instances on the
 * server). Centralised here so the queue handler, the neighbors handler,
 * the socket broadcast, and the React Query cache invalidator all agree
 * on the field names.
 */
export interface ReviewClaim {
  /** Whatever the websocket layer + handlers use as the canonical user id (currently the user's email per the existing requireAdmin path). */
  claimerId: string;
  /** Display name for "Sarah is reviewing this delivery" UI. */
  claimerName: string;
  /** Wall-clock time the claim was opened or last refreshed. */
  claimedAt: string;
}

// SPEC-annotation-review-concurrency Phase 5 — wire-shape types for the
// review:* socket events. Centralised here so the server emit-side
// (server/websocket.ts) and the client hook surface (client/src/hooks/
// use-annotation-review-socket.ts) can't drift on field names.

/** Reason a release happened, surfaced on broadcast events so the UI can
 *  decide whether to show a toast (took_over) or just refresh the queue
 *  (disconnect, timeout). */
export type ReviewReleaseReason = "disconnect" | "timeout" | "took_over";

/** review:claim payload (client → server). */
export interface ReviewClaimRequest {
  deliveryId: string;
  /** When true, override an existing active claim (the take-over flow). */
  force?: boolean;
}

/** review:claimed payload (server → claiming socket on success). */
export interface ReviewClaimedAck {
  deliveryId: string;
  claimerId: string;
  claimerName: string;
  /** ISO-8601 string. */
  claimedAt: string;
  /** Set ONLY on a successful force=true claim where someone else previously held it. */
  tookOverFrom?: string;
}

/** review:claim_failed payload (server → claiming socket on conflict). */
export interface ReviewClaimFailed {
  deliveryId: string;
  claimerId: string | null;
  claimerName: string | null;
  claimedAt: string | null;
  message?: string;
}

/** review:release payload (client → server). */
export interface ReviewReleaseRequest {
  deliveryId: string;
}

/** review:released payload (server → caller of review:release on success). */
export interface ReviewReleasedAck {
  deliveryId: string;
}

/** review:update payload (server → all clients, broadcast). */
export interface ReviewUpdateEvent {
  type: "claimed" | "released" | "verdict_changed";
  deliveryId: string;
  claimerId?: string | null;
  claimerName?: string | null;
  claimedAt?: string | null;
  /** Set on verdict_changed events. */
  regionReviewStatus?: string;
  /** Set on released events. Also set on claimed events when type === "claimed" and reason === "took_over" so the displaced reviewer's UI can toast "Claimed by <name>". */
  reason?: ReviewReleaseReason;
}

/**
 * SPEC-annotation-review-concurrency Phase 3 — body shape returned by the
 * PATCH endpoint when the If-Match-Claim header doesn't match the active
 * claimer (HTTP 409). Used by the client hook's 409 handler to populate
 * the displaced-reviewer banner with the actual claimer's info.
 */
export interface ReviewClaimConflictBody {
  claimedBy: string;
  claimedByName: string | null;
  claimedAt: string | null;
}

/** HTTP header that carries the version-check token on PATCH requests from review mode. */
export const IF_MATCH_CLAIM_HEADER = "If-Match-Claim";
